import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { useCreation } from '../../functions/hooks/useCreation';
import { usePersistFn } from '../../functions/hooks/usePersistFn';

export type ClientSearchFunction = () => any;

type ClientSearch = {
  search: ClientSearchFunction;
};

export type GlobalSearchContextProps = {
  query$: BehaviorSubject<string>;
  client$: BehaviorSubject<ClientSearch | undefined>;
};

export const GlobalSearchContext = createContext<GlobalSearchContextProps>(
  null as any,
);

export const useGlobalSearchContext = () => {
  return useContext(GlobalSearchContext);
};

export const GlobalSearchContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const query$ = useCreation(() => new BehaviorSubject<string>(''), []);
    const client$ = useCreation(
      () => new BehaviorSubject<ClientSearch | undefined>(undefined),
      [],
    );
    const value = useMemo(() => ({ query$, client$ }), [query$, client$]);
    return (
      <GlobalSearchContext.Provider value={value}>
        {children}
      </GlobalSearchContext.Provider>
    );
  },
);

export const useGlobalSearchClient = (
  search: ClientSearchFunction,
  focused: boolean,
) => {
  const { client$ } = useGlobalSearchContext();
  const stableSearch = usePersistFn(search);
  useEffect(() => {
    client$.next(focused ? { search: stableSearch } : undefined);
  }, [client$, focused, stableSearch]);
};
